// =======================================================
// DataStream — scrolling rows of characters, Matrix-style but horizontal
// =======================================================
//
// Technique: we render the characters to a regular 2D canvas once per
// frame (cheap — it's just text), upload as a texture, and display it
// fullscreen. This gives us crisp real glyphs without font-atlas pain.
//
// On 10900×540 we might render 100+ rows of short characters — 2D canvas
// at this size is still fast for text because the character set is small
// and text rendering is GPU-accelerated in modern browsers.

import * as THREE from 'three';
import { Generator } from './Generator.js';

const VERT = /* glsl */ `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }
`;
const FRAG = /* glsl */ `
  varying vec2 vUv;
  uniform sampler2D uMap;
  uniform vec3 uColor;
  uniform vec3 uBg;
  void main() {
    vec4 t = texture2D(uMap, vUv);
    // canvas gave us alpha in R channel (we drew white text on transparent bg)
    float m = t.r;
    gl_FragColor = vec4(mix(uBg, uColor, m), 1.0);
  }
`;

const CHARSETS = {
  hex:   '0123456789ABCDEF',
  bin:   '01',
  kata:  'ｱｲｳｴｵｶｷｸｹｺｻｼｽｾｿﾀﾁﾂﾃﾄﾅﾆﾇﾈﾉﾊﾋﾌﾍﾎﾏﾐﾑﾒﾓﾔﾕﾖﾗﾘﾙﾚﾛﾜｦﾝ',
  ai:    'AIxDAY2026×',
  sym:   '◆◇●○■□▲△▼▽◀▶♦♠♣♥⬡⬢⚡',
  code:  '{}[]()<>;/\\*+-_=|&%#@!?'
};

export class DataStream extends Generator {
  constructor() {
    super({
      name: 'Data Stream',
      category: 'intense',
      schema: {
        rows:      { default: 18,   min: 4,    max: 80,   step: 1,    label: 'Rows' },
        speed:     { default: 4000, min: 100,  max: 10000, step: 10,  label: 'Scroll speed' },
        fontPx:    { default: 24,   min: 8,    max: 80,   step: 1,    label: 'Font size (px)' },
        density:   { default: 0.85, min: 0.1,  max: 1,    step: 0.01, label: 'Density' },
        fade:      { default: 0.7,  min: 0,    max: 1,    step: 0.01, label: 'Tail fade' },
        flicker:   { default: 0.18, min: 0,    max: 1,    step: 0.01, label: 'Flicker' },
        charset:   { default: 'hex', type: 'select', label: 'Characters',
                     options: [['hex','HEX 0-F'],['bin','Binary'],['kata','Katakana'],
                               ['ai','AI×DAY'],['sym','Symbols'],['code','Code']] },
        bidi:      { default: true, type: 'bool', label: 'Alternate directions' },
        accent:    { default: '#62ffd8', type: 'color', label: 'Color' },
      }
    });
  }

  onEnter(scene, engine) {
    this.engine = engine;

    // offscreen canvas for rasterizing characters
    this.off = document.createElement('canvas');
    this.offCtx = this.off.getContext('2d', { alpha: true });
    this._resizeTextCanvas(engine.width, engine.height);

    // texture + material
    this.tex = new THREE.CanvasTexture(this.off);
    this.tex.minFilter = THREE.LinearFilter;
    this.tex.magFilter = THREE.LinearFilter;
    this.tex.generateMipmaps = false;

    const geo = new THREE.PlaneGeometry(2, 2);
    const mat = new THREE.ShaderMaterial({
      vertexShader: VERT, fragmentShader: FRAG,
      uniforms: {
        uMap:   { value: this.tex },
        uColor: { value: new THREE.Color(this.v('accent')) },
        uBg:    { value: new THREE.Color('#000000') },
      },
      transparent: false,
      depthWrite: false,
      depthTest: false
    });
    this.mesh = new THREE.Mesh(geo, mat);
    this.mesh.frustumCulled = false;
    scene.add(this.mesh);
    this.track(this.mesh);
    this._rowOffsets = [];
    this._seedRows();
  }

  _seedRows() {
    const n = Math.floor(this.v('rows'));
    this._rowOffsets = [];
    for (let i = 0; i < n; i++) {
      this._rowOffsets.push({
        phase: this.rand01(100 + i * 7),
        speedMul: this.randRange(101 + i * 7, 0.5, 1.5),
        brightness: this.randRange(102 + i * 7, 0.7, 1.0),
        dir: (this.v('bidi') && (i % 2 === 0)) ? -1 : 1,
      });
    }
  }

  reseed() { this._seedRows(); }

  _resizeTextCanvas(w, h) {
    const maxTextWidth = 4096;
    const maxTextHeight = 1080;
    this.off.width = Math.max(2, Math.min(maxTextWidth, Math.round(w)));
    this.off.height = Math.max(2, Math.min(maxTextHeight, Math.round(h)));
  }

  onResize(w, h) {
    this._resizeTextCanvas(w, h);
    // no need to resend uniform — CanvasTexture tracks it
  }

  onParamChange(key, val) {
    if (key === 'rows' || key === 'bidi') this._seedRows();
    if (key === 'accent' && this.mesh) {
      this.mesh.material.uniforms.uColor.value.set(val);
    }
  }

  update(dt, time, engine) {
    const v = this.values;
    const ctx = this.offCtx;
    const w = this.off.width, h = this.off.height;
    const loop = engine.getLoopInfo(time);

    // full clear each frame — composer trail-mesh handles global trails
    ctx.clearRect(0, 0, w, h);

    const chars = CHARSETS[v.charset] || CHARSETS.hex;
    const rows = Math.floor(v.rows);
    const rowH = h / rows;
    const fontPx = Math.min(v.fontPx, rowH * 1.1);
    ctx.font = `100 ${fontPx}px 'JetBrains Mono', ui-monospace, monospace`;
    ctx.textBaseline = 'middle';
    const cellW = fontPx * 0.62;
    const cols = Math.ceil(w / cellW) + 2;

    for (let r = 0; r < rows; r++) {
      const ro = this._rowOffsets[r];
      if (!ro) continue;
      const scrollCells = Math.max(0.2, 4000 / Math.max(1, v.speed)) * ro.speedMul;
      const offCells = (ro.phase + loop.phase * scrollCells) * cols;
      const signedOffCells = ro.dir > 0 ? offCells : -offCells;
      const start = Math.floor(signedOffCells);
      const xShift = - (signedOffCells - start) * cellW;
      const y = (r + 0.5) * rowH;
      for (let i = 0; i < cols; i++) {
        const idx = start + i;
        // deterministic char per (row, cell) within a large period so
        // the stream "remembers" its content as it scrolls
        const hash = ((idx * 2654435761) ^ (r * 1597334677)) >>> 0;
        const vis = (((hash ^ (this.seed * 2246822519)) >>> 0) / 4294967295);
        if (vis > v.density) continue;
        const ch = chars[hash % chars.length];
        const headPos = ro.dir > 0 ? cols - 1 : 0;
        const isHead = i === headPos;
        const distFromHead = Math.abs(i - headPos) / cols;
        const fade = 1 - distFromHead * v.fade;
        const beatHash = (((hash ^ ((loop.beatIndex + 1) * 1103515245) ^ this.seed) >>> 0) / 4294967295);
        const flk = 1 - beatHash * v.flicker;
        const alpha = Math.max(0, fade * flk * ro.brightness);

        if (isHead) {
          // bright white head
          ctx.fillStyle = `rgba(255,255,255,${Math.max(0.7, alpha)})`;
        } else {
          // we output grayscale (we use only R channel in shader, remap to color)
          ctx.fillStyle = `rgba(255,255,255,${alpha})`;
        }
        ctx.fillText(ch, i * cellW + xShift, y);
      }
    }

    this.tex.needsUpdate = true;
  }
}
